import { ALargeSmall, Monitor, Moon, Sun } from 'lucide-react'
import { Slider } from '@/shared/components/ui/slider'
import { Label } from '@/shared/components/ui/label'
import { useDarkMode, type Theme } from '@/shared/hooks/useDarkMode'
import { useContrast, type Contrast } from '@/shared/hooks/useContrast'
import { useTextSize } from '@/shared/hooks/useTextSize'

export function AppearanceSettings() {
  const { theme, setTheme } = useDarkMode()
  const { contrast, setContrast } = useContrast()
  const { textSize, setTextSize } = useTextSize()

  return (
    <>
      <div className="mb-6">
        <p className="text-sm font-medium font-sans text-foreground mb-4">Theme</p>
        <div className="grid grid-cols-3 gap-4">
          {(
            [
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'system', label: 'System', icon: Monitor },
            ] as const
	          ).map(({ value, label, icon: Icon }) => {
	            const isActive = theme === value
	            const previewIsDark =
	              value === 'dark' ||
	              (value === 'system' &&
	                window.matchMedia('(prefers-color-scheme: dark)').matches)
	            const preview = previewIsDark
	              ? {
	                  shell: 'var(--background)',
	                  sidebar: 'var(--brand-950)',
	                  mark: 'var(--brand-800)',
	                  panel: 'var(--card)',
	                  border: 'var(--border)',
	                  line: 'var(--muted)',
	                }
	              : {
	                  shell: 'var(--surface-1)',
	                  sidebar: 'var(--surface-2)',
	                  mark: 'var(--neutral-300)',
	                  panel: 'var(--surface-0)',
	                  border: 'var(--border)',
	                  line: 'var(--surface-2)',
	                }
	            return (
              <button
                key={value}
                type="button"
                onClick={() => setTheme(value as Theme)}
                className={`group rounded-lg border-2 overflow-hidden transition-colors text-left ${
                  isActive
                    ? 'border-primary ring-1 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
	                <div className="p-3" style={{ backgroundColor: preview.shell }}>
	                  <div className="flex gap-1.5 h-[72px]">
	                    <div
	                      className="w-8 rounded flex flex-col gap-1 p-1"
	                      style={{ backgroundColor: preview.sidebar }}
	                    >
	                      <div
	                        className="h-1 w-full rounded-full"
	                        style={{ backgroundColor: preview.mark }}
	                      />
	                      <div
	                        className="h-1 w-full rounded-full"
	                        style={{ backgroundColor: preview.mark }}
	                      />
	                      <div
	                        className="h-1 w-3/4 rounded-full"
	                        style={{ backgroundColor: preview.mark }}
	                      />
	                    </div>
	                    <div className="flex-1 flex flex-col gap-1">
	                      <div
	                        className="h-2 w-1/3 rounded"
	                        style={{ backgroundColor: preview.border }}
	                      />
	                      <div
	                        className="flex-1 rounded border p-1.5 flex flex-col gap-1"
	                        style={{
	                          backgroundColor: preview.panel,
	                          borderColor: preview.border,
	                        }}
	                      >
	                        <div
	                          className="h-1 w-full rounded-full"
	                          style={{ backgroundColor: preview.line }}
	                        />
	                        <div
	                          className="h-1 w-4/5 rounded-full"
	                          style={{ backgroundColor: preview.line }}
	                        />
	                        <div
	                          className="h-1 w-2/3 rounded-full"
	                          style={{ backgroundColor: preview.line }}
	                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-2 px-3 py-2.5 border-t ${
                    isActive ? 'border-primary/20 bg-primary/5' : 'border-border'
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`text-sm font-medium font-sans ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm font-medium font-sans text-foreground mb-4">Contrast</p>
        <div className="grid grid-cols-2 gap-4 max-w-[340px]">
          {(
            [
              {
                value: 'regular',
                label: 'Regular',
                previewBg: 'var(--surface-1)',
                previewFg: 'var(--neutral-950)',
                previewBorder: 'var(--border)',
              },
              {
                value: 'high',
                label: 'High Contrast',
                previewBg: '#ffffff',
                previewFg: '#000000',
                previewBorder: '#000000',
              },
            ] as const
          ).map(({ value, label, previewBg, previewFg, previewBorder }) => {
            const isActive = contrast === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => setContrast(value as Contrast)}
                className={`group rounded-lg border-2 overflow-hidden transition-colors text-left ${
                  isActive
                    ? 'border-primary ring-1 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
                <div className="p-3" style={{ backgroundColor: previewBg }}>
                  <div className="flex flex-col gap-1.5 h-[40px] justify-center px-1">
                    <div
                      className="h-1.5 w-full rounded-full"
                      style={{ backgroundColor: previewFg, opacity: 0.8 }}
                    />
                    <div
                      className="h-1.5 w-3/4 rounded-full"
                      style={{ backgroundColor: previewFg, opacity: 0.5 }}
                    />
                    <div className="h-px w-full" style={{ backgroundColor: previewBorder }} />
                  </div>
                </div>
                <div
                  className={`flex items-center justify-center px-3 py-2.5 border-t ${
                    isActive ? 'border-primary/20 bg-primary/5' : 'border-border'
                  }`}
                >
                  <span
                    className={`text-sm font-medium font-sans ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-2">
        <Label className="text-sm font-medium font-sans text-foreground">Text size</Label>
        <div className="flex items-center gap-3 mt-3">
          <ALargeSmall className="h-4 w-4 text-muted-foreground shrink-0" />
          <Slider
            value={[['small', 'medium', 'large'].indexOf(textSize)]}
            min={0}
            max={2}
            step={1}
            onValueChange={([v]) =>
              setTextSize((['small', 'medium', 'large'] as const)[v])
            }
            className="w-48"
          />
          <ALargeSmall className="h-6 w-6 text-muted-foreground shrink-0" />
        </div>
        <div className="flex justify-between w-48 ml-7 mt-1.5">
          {(['Small', 'Medium', 'Large'] as const).map((label) => (
            <span key={label} className="text-[11px] text-muted-foreground font-sans">
              {label}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}
